/**
 * @fileoverview Provides utilities for handling and processing file diffs, including an abstract base class for AI-powered diff processing. Supports streaming diff analysis, custom prompting, and various diff transformation utilities for working with code changes.
 */

import Diff = require('diff')
import { Message, Model } from '@iterator/shared-node/ai/llm_types'
import { debug, diff_block, error, info } from '@iterator/shared-node/util/logging'
import { changesToString, combineChanges, truncateDiffChanges } from './rewrite'
import { streamToConsole } from './stream'

export type PathDiff = { path: string; changes: Diff.Change[]; before: string; after: string }
export type OnDiff = (diff: PathDiff) => Promise<void>
export interface OnDiffReceiver {
  onDiff: OnDiff
  mostRecentResult: string | undefined
}
export type OnDiffOptions = {
  shouldPrintDiff: boolean
  shouldPrintStream: boolean
}

export const printOnDiff: OnDiff = async (diff) => {
  diff_block(diff.path, changesToString(truncateDiffChanges(diff.changes, 5)))
}

export abstract class BaseLlmOnDiff implements OnDiffReceiver {
  mostRecentResult: string | undefined = undefined
  priorDiffs: { timestamp: number; diffs: PathDiff[] }[] = []
  private currentlyStreaming = false

  constructor(
    private model: Model,
    public options: OnDiffOptions,
  ) {}

  async onDiff(diff: PathDiff) {
    if (this.currentlyStreaming) {
      debug('SuggestOnDiff: Already streaming, skipping')
      return
    }
    this.currentlyStreaming = true

    this.onNewDiff()
    if (this.options.shouldPrintDiff) {
      await printOnDiff(diff)
    }

    if (this.shouldSkip(diff)) {
      this.currentlyStreaming = false
      info(`Skipping diff for ${diff.path}`)
      return
    }

    try {
      const { message } = await streamToConsole(
        this.model,
        this.getSystemPrompt(),
        this.getTexts(diff),
        this.getUserPrompt(diff),
        this.options.shouldPrintStream,
        this.getPrediction(diff),
      )
      this.onMessage(diff, message)
    } catch (e) {
      error('Error streaming suggestions', e)
    } finally {
      this.priorDiffs.push({ timestamp: Date.now(), diffs: [diff] })
      this.currentlyStreaming = false
    }
  }

  abstract getSystemPrompt(): string
  abstract getTexts(diff: PathDiff): string[]
  abstract getUserPrompt(diff: PathDiff): string

  getPrediction(_diff: PathDiff): string | undefined {
    return undefined
  }

  shouldSkip(_diff: PathDiff) {
    return false
  }

  onNewDiff() {
    // No-op default implementation
  }

  onMessage(_pathDiff: PathDiff, _message: Message) {
    // No-op default implementation
  }
}

export function diffPathToChangeString(
  diff: PathDiff,
  truncate: number | undefined = undefined,
): string {
  return `<file_path>${diff.path}</file_path>\n<file_changes>\n${diffToString(
    diff.changes,
    truncate,
  )}\n</file_changes>`
}

export function diffToString(
  changes: Diff.Change[],
  truncate: number | undefined = undefined,
): string {
  return changesToString(dedentChanges(truncate ? truncateDiffChanges(changes, truncate) : changes))
}

export function diffToCodeBlock(
  changes: Diff.Change[],
  truncate: number | undefined = undefined,
): string {
  return combineChanges(truncate ? truncateDiffChanges(changes, truncate) : changes)
}

function dedentChanges(changes: Diff.Change[]): Diff.Change[] {
  // Find minimum indentation across all changes
  const minIndent = Math.min(
    ...changes
      .map((change) => {
        const lines = change.value.split('\n').filter((line) => line.trim().length > 0)
        return Math.min(
          ...lines.map((line) => {
            const match = line.match(/^[ \t]*/)
            return match ? match[0].length : 0
          }),
        )
      })
      .filter((indent) => indent !== Infinity),
  )

  if (minIndent === 0) {
    return changes
  }

  return changes.map((change) => ({
    ...change,
    value: change.value
      .split('\n')
      .map((line) => {
        if (line.trim().length === 0) {
          return line
        }
        return line.slice(minIndent)
      })
      .join('\n'),
  }))
}
